/**
 * Thrown tridents (vanilla ThrownTrident): 2.5 b/t throw, gravity 0.05, drag 0.99
 * (0.99 in water too — tridents keep their speed underwater), 8 damage (+ Impaling
 * vs aquatic, none here) on the first entity hit, then they bounce back and drop;
 * stick in blocks and can be picked up by walking into them. Loyalty: after
 * hitting something the trident flies back to its owner (vanilla: 0.05 * level
 * velocity pull per tick) and returns to the inventory.
 * Self-contained so main.ts can own it without touching the EntityManager.
 */

import { collisionOf } from '../blocks';
import { E } from '../game/enchant/registry';
import { enchLevel } from '../game/enchant/stack';
import { cloneStack, type Stack } from '../game/types';
import type { World } from '../world/chunk';
import { segmentAabb } from './host';
import type { Mob } from './mob';

export interface TridentView {
  x: number; y: number; z: number; px: number; py: number; pz: number;
  yaw: number; pitch: number; light: number; enchanted: boolean;
}

export interface TridentHost {
  world: World;
  mobs: readonly Mob[];
  player: { x: number; y: number; z: number; alive: boolean };
  /** Melee-style hit on a mob; returns whether it landed. */
  hit(mob: Mob, damage: number, fromX: number, fromZ: number): boolean;
  /** Is the mob in water or rain (Bedrock Impaling)? */
  isWet?(mob: Mob): boolean;
  /** Give the item back (to its original hotbar slot when free); returns false if the inventory is full. */
  give(s: Stack, slot?: number): boolean;
  /** Channeling: a thrown trident hit a mob (thunder + open sky strikes lightning at it). */
  channel?(mob: Mob): void;
  light(x: number, y: number, z: number): number;
  sound?(id: string, x: number, y: number, z: number): void;
}

/** Thrown trident damage: 8, + Impaling 2.5/level on Bedrock when the target is in water or rain. */
export const tridentDamage = (impaling: number, wet: boolean): number => 8 + (wet ? 2.5 * impaling : 0);

export class ThrownTrident {
  x: number; y: number; z: number;
  px: number; py: number; pz: number;
  vx: number; vy: number; vz: number;
  stuck = false;
  dealt = false;
  returning = false;
  removed = false;
  life = 0;
  private returnTicks = 0;
  readonly loyalty: number;
  readonly view: TridentView;

  constructor(readonly stack: Stack, x: number, y: number, z: number, vx: number, vy: number, vz: number, readonly creative: boolean,
    /** Hotbar slot it was thrown from (Loyalty / pickup put it back there). */ readonly slot = -1) {
    this.stack = cloneStack(stack);
    this.x = this.px = x; this.y = this.py = y; this.z = this.pz = z;
    this.vx = vx; this.vy = vy; this.vz = vz;
    this.loyalty = enchLevel(stack, E.LOYALTY);
    this.view = { x, y, z, px: x, py: y, pz: z, yaw: 0, pitch: 0, light: 1, enchanted: !!stack.ench?.length };
    this.orient();
  }

  private orient(): void {
    const h = Math.hypot(this.vx, this.vz);
    if (h > 1e-6 || Math.abs(this.vy) > 1e-6) {
      this.view.yaw = Math.atan2(-this.vx, -this.vz);
      this.view.pitch = Math.atan2(this.vy, h);
    }
  }

  private inBlock(w: World, x: number, y: number, z: number): boolean {
    const bx = Math.floor(x), by = Math.floor(y), bz = Math.floor(z);
    const sh = collisionOf(w.getBlock(bx, by, bz));
    if (!sh.length) return false;
    const lx = (x - bx) * 16, ly = (y - by) * 16, lz = (z - bz) * 16;
    for (let s = 0; s < sh.length; s += 6) {
      if (lx >= sh[s] && lx <= sh[s + 3] && ly >= sh[s + 1] && ly <= sh[s + 4] && lz >= sh[s + 2] && lz <= sh[s + 5]) return true;
    }
    return false;
  }

  tick(h: TridentHost): void {
    this.px = this.x; this.py = this.y; this.pz = this.z;
    this.life++;
    const p = h.player;
    // pickup: owner touching it (stuck, returning, or lying after a hit)
    const near = Math.abs(p.x - this.x) < 1.3 && Math.abs(p.z - this.z) < 1.3 && this.y > p.y - 1 && this.y < p.y + 2.3;
    if (p.alive && near && (this.stuck || this.returning || this.dealt) && this.life > 5) {
      if (this.creative || h.give(this.stack, this.slot)) { this.removed = true; h.sound?.('item.trident.return', this.x, this.y, this.z); return; }
    }
    if ((this.dealt || this.stuck) && this.loyalty > 0 && p.alive) this.returning = true;
    if (this.returning) {
      // vanilla: noPhysics, pulled toward the owner's eyes
      this.stuck = false;
      const dx = p.x - this.x, dy = p.y + 1.62 - this.y, dz = p.z - this.z;
      const len = Math.hypot(dx, dy, dz) || 1;
      const k = 0.05 * this.loyalty; // vanilla pull: v = 0.95 v + 0.05 * level
      if (this.returnTicks++ === 0) h.sound?.('item.trident.return', this.x, this.y, this.z);
      this.vx = this.vx * 0.95 + (dx / len) * k;
      this.vy = this.vy * 0.95 + (dy / len) * k;
      this.vz = this.vz * 0.95 + (dz / len) * k;
      this.x += this.vx; this.y += this.vy; this.z += this.vz;
      this.orient();
      this.sync(h);
      return;
    }
    if (this.stuck) { this.sync(h); return; }
    // Loyalty brings it back from the void; otherwise it is lost
    if (this.y < -10 && this.loyalty > 0 && p.alive) { this.returning = true; return; }
    if (this.life > 1200 || this.y < -64) { this.removed = true; return; }

    const dx = this.vx, dy = this.vy, dz = this.vz;
    const len = Math.hypot(dx, dy, dz);
    let blockT = 2;
    const steps = Math.max(1, Math.ceil(len / 0.2));
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      if (this.inBlock(h.world, this.x + dx * t, this.y + dy * t, this.z + dz * t)) { blockT = t; break; }
    }
    let hitMob: Mob | null = null, entT = 2;
    if (!this.dealt) {
      for (const m of h.mobs) {
        if (!m.alive) continue;
        const hw = m.width / 2 + 0.3;
        const t = segmentAabb(this.x, this.y, this.z, dx, dy, dz, [m.x - hw, m.y - 0.3, m.z - hw, m.x + hw, m.y + m.height + 0.3, m.z + hw]);
        if (t >= 0 && t < entT) { entT = t; hitMob = m; }
      }
    }
    if (hitMob && entT <= 1 && entT <= blockT) {
      this.x += dx * entT; this.y += dy * entT; this.z += dz * entT;
      h.hit(hitMob, tridentDamage(enchLevel(this.stack, E.IMPALING), h.isWet?.(hitMob) ?? false), this.x - dx, this.z - dz);
      h.sound?.('item.trident.hit', this.x, this.y, this.z);
      if (enchLevel(this.stack, E.CHANNELING) > 0) h.channel?.(hitMob);
      this.dealt = true;
      // vanilla: bounce back off the target and fall
      this.vx *= -0.01; this.vy *= -0.1; this.vz *= -0.01;
      this.sync(h);
      return;
    }
    if (blockT <= 1) {
      this.x += dx * blockT; this.y += dy * blockT; this.z += dz * blockT;
      this.stuck = true;
      this.vx = this.vy = this.vz = 0;
      h.sound?.('item.trident.hit_ground', this.x, this.y, this.z);
      this.sync(h);
      return;
    }
    this.x += dx; this.y += dy; this.z += dz;
    this.vx *= 0.99; this.vy *= 0.99; this.vz *= 0.99;
    this.vy -= 0.05;
    this.orient();
    this.sync(h);
  }

  private sync(h: TridentHost): void {
    const v = this.view;
    v.x = this.x; v.y = this.y; v.z = this.z; v.px = this.px; v.py = this.py; v.pz = this.pz;
    v.light = h.light(this.x, this.y, this.z);
  }
}

export class TridentManager {
  readonly list: ThrownTrident[] = [];
  throw(t: ThrownTrident): void { this.list.push(t); }
  tick(h: TridentHost): void {
    for (const t of this.list) t.tick(h);
    for (let i = this.list.length - 1; i >= 0; i--) if (this.list[i].removed) this.list.splice(i, 1);
  }
  views(): TridentView[] { return this.list.map((t) => t.view); }
}
